import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "buttonContent"]
  static values = {
    url: String,
    statusUrl: String,
    activityId: String
  }

  connect() {
    // Listen for document-generation-complete event from broadcast to reset button
    this.boundHandleGenerated = this.handleGenerated.bind(this)
    window.addEventListener("document-generation-complete", this.boundHandleGenerated)

    // Polling interval (in milliseconds)
    this.pollingInterval = null
  }

  disconnect() {
    window.removeEventListener("document-generation-complete", this.boundHandleGenerated)
    this.stopPolling()
  }

  async generate(event) {
    event.preventDefault()

    // Show loading state
    this.showLoading()

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.already) {
          window.dispatchEvent(new CustomEvent('toast:show', {
            detail: { message: data.message || 'Document generation is already in progress', type: 'info' }
          }))
        }
        // Job queued successfully, show processing state and start polling
        this.showProcessingState()
        this.startPolling()
      } else {
        const error = await response.json()
        this.showError(error.error || 'Failed to generate document')
        this.resetButton()
      }
    } catch (error) {
      this.showError('An error occurred. Please try again.')
      this.resetButton()
    }
  }

  handleGenerated(event) {
    // Only reset if this is for our activity
    if (event.detail.activityId === this.activityIdValue) {
      this.stopPolling()
      this.resetButton()
      this.showSuccess('Document generated successfully!')

      // Reload page to show the generated document
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    }
  }

  startPolling() {
    // Poll every 3 seconds to check job status
    this.pollingInterval = setInterval(() => {
      this.checkJobStatus()
    }, 3000)
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  async checkJobStatus() {
    try {
      const response = await fetch(this.statusUrlValue, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()

        if (data.status === 'completed') {
          // Job finished successfully - reload page to show new document
          this.stopPolling()
          this.resetButton()
          this.showSuccess('Document generated successfully!')

          // Reload the page to show updated document
          setTimeout(() => {
            window.location.reload()
          }, 1000)
        } else if (data.status === 'idle') {
          // No job running (might have been cleared)
          this.stopPolling()
          this.resetButton()
        } else if (data.status === 'failed') {
          // Job failed in Solid Queue
          this.stopPolling()
          this.showError(data.error || 'Failed to generate document')
          this.resetButton()
        } else if (data.status === 'error') {
          // Job failed with error
          this.stopPolling()
          this.showError(data.error || data.message || 'Failed to generate document')
          this.resetButton()
        }
        // If status is 'processing', keep polling
      }
    } catch (error) {
      // Don't stop polling on network errors, might be temporary
    }
  }

  showLoading() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
    }

    // Update button content to show loading
    if (this.hasButtonContentTarget) {
      this.originalContent = this.buttonContentTarget.innerHTML
      this.buttonContentTarget.innerHTML = `
        <svg class="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8.54661 19.7675C10.9457 20.8319 13.8032 20.7741 16.2502 19.3613C20.3157 17.0141 21.7086 11.8156 19.3614 7.75008L19.1114 7.31706M4.63851 16.2502C2.2913 12.1847 3.68424 6.98619 7.74972 4.63898C10.1967 3.22621 13.0542 3.16841 15.4533 4.23277M2.49341 16.3338L5.22546 17.0659L5.95751 14.3338M18.0426 9.6659L18.7747 6.93385L21.5067 7.6659"/>
        </svg>
        <span>Generating...</span>
      `
    }
  }

  showProcessingState() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
    }

    // Update button content to show processing
    if (this.hasButtonContentTarget) {
      this.buttonContentTarget.innerHTML = `
        <svg class="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8.54661 19.7675C10.9457 20.8319 13.8032 20.7741 16.2502 19.3613C20.3157 17.0141 21.7086 11.8156 19.3614 7.75008L19.1114 7.31706M4.63851 16.2502C2.2913 12.1847 3.68424 6.98619 7.74972 4.63898C10.1967 3.22621 13.0542 3.16841 15.4533 4.23277M2.49341 16.3338L5.22546 17.0659L5.95751 14.3338M18.0426 9.6659L18.7747 6.93385L21.5067 7.6659"/>
        </svg>
        <span>Processing...</span>
      `
    }
  }

  resetButton() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = false
    }

    // Restore original button content
    if (this.hasButtonContentTarget && this.originalContent) {
      this.buttonContentTarget.innerHTML = this.originalContent
    }
  }

  showError(message) {
    // Show toast notification
    const event = new CustomEvent('toast:show', {
      detail: {
        message: message,
        type: 'error'
      }
    })
    window.dispatchEvent(event)
  }

  showSuccess(message) {
    // Show toast notification
    const event = new CustomEvent('toast:show', {
      detail: {
        message: message,
        type: 'success'
      }
    })
    window.dispatchEvent(event)
  }
}
